import React, { useState, useEffect, useRef } from "react";
import { InlineMath } from "react-katex";
import SetKeyboard from "./SetKeyboard";
import { normalizePreviewLabel } from "./previewLabel";
import { parseFractionValue } from "../../utils/fractionValue";

/* ====== Stałe layoutu ====== */
const INDENT = 36;               // px – kolumna na ikonkę + odstęp
const PANEL_WIDTH_DESKTOP = 250; // px - kompaktowa szerokosc pola i panelu (bez INDENT)

/* =================== Helpers =================== */

function parseFractionLikeInput(input) {
  return parseFractionValue(input);
}

function formatLatexPreview(input, label = "") {
  label = normalizePreviewLabel(label);
  if (!input) return null;
  const cleaned = input.trim().replace(",", ".");
  const parts = cleaned.split(" ");
  if (parts.length === 2 && parts[1].includes("/")) {
    const whole = parseInt(parts[0]);
    const [n, d] = parts[1].split("/");
    const absN = Math.abs(parseInt(n));
    const sign = cleaned.startsWith("-") ? "-" : "";
    const valueLatex = `${sign}${Math.abs(whole)}\\dfrac{${absN}}{${d}}`;
    return label ? `${label} = ${valueLatex}` : valueLatex;
  }
  if (cleaned.includes("/")) {
    const [n, d] = cleaned.split("/");
    const valueLatex = `\\dfrac{${n}}{${d}}`;
    return label ? `${label} = ${valueLatex}` : valueLatex;
  }
  const valueLatex = `${cleaned}`;
  return label ? `${label} = ${valueLatex}` : valueLatex;
}

function formatFractionLatexCore(input) {
  if (!input) return null;
  const cleaned = input.trim().replace(",", ".");
  const parts = cleaned.split(" ");
  if (parts.length === 2 && parts[1].includes("/")) {
    const whole = parseInt(parts[0]);
    const [n, d] = parts[1].split("/");
    const absN = Math.abs(parseInt(n));
    const sign = cleaned.startsWith("-") ? "-" : "";
    return `${sign}${Math.abs(whole)}\\dfrac{${absN}}{${d}}`;
  }
  if (cleaned.includes("/")) {
    const [n, d] = cleaned.split("/");
    return `\\dfrac{${n}}{${d}}`;
  }
  return `${cleaned}`;
}

/* Skróty potęgowe → LaTeX (tylko gdy CAŁY input pasuje) */
function formatPowerLatexIfMatch(input) {
  if (!input) return null;
  const s = String(input).trim().replace(/\s+/g, "");

  // 1) 1/(a^b)
  let m = s.match(/^1\/\((\d+)\^(\d+)\)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;

  // 2) 1/a^b
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;

  // 3) a^(-b) lub a^-b
  m = s.match(/^(\d+)\^\-(\d+)$/);
  if (m) return `${m[1]}^{-${m[2]}}`;

  // 4) a^(p/q) (z nawiasem)
  m = s.match(/^(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) return `${m[1]}^{\\tfrac{${m[2]}}{${m[3]}}}`;

  // 5) a^p/q lub a^-p/q (bez nawiasu)
  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const base = m[1], sign = s.includes("^-") ? "-" : "", p = m[2], q = m[3];
    return `${base}^{\\tfrac{${sign}${p}}{${q}}}`;
  }

  // 6) ⬅️ brakujący przypadek: a^b (b > 0, całkowity)
  m = s.match(/^(\d+)\^(\d+)$/);
  if (m) return `${m[1]}^{${m[2]}}`;

  return null;
}


/* Notacja wykładnicza → LaTeX (tylko gdy CAŁY input pasuje) */
function formatSciLatexIfMatch(input) {
  if (!input) return null;
  const s = String(input).trim().replace(/\s/g, "");
  let m = s.match(/^(\d+(?:[.,]\d+)?)(?:\*|·)?10\^\(?([+\-]?\d+)\)?$/i);
  if (m) return `${m[1].replace(".", ",")}\\,\\cdot\\,10^{${m[2]}}`;
  m = s.match(/^(\d+(?:[.,]\d+)?)[eE]([+\-]?\d+)$/);
  if (m) return `${m[1].replace(".", ",")}\\,\\cdot\\,10^{${m[2]}}`;
  return null;
}

// Rozpoznaj i normalizuj wpisy całkowite z separatorami tysięcy
function normalizeIntegerLikeRaw(input) {
  if (!input) return "";
  const s = String(input);

  // 1) Jeśli to ułamek zwykły/mieszany (jest "/") — nic nie zmieniamy
  if (s.includes("/")) return s;

  // 2) Jeśli wygląda na LICZBĘ MIESZANĄ "w trakcie" (np. "1 ␣", "1 ␣ 1", "1 ␣ 1/")
  //    to zostaw spację, żeby dało się dopisać część ułamkową
  if (/^[+\-]?\d+\s+\d*\/?\d*$/.test(s)) return s;

  // 3) W pozostałych przypadkach: usuń wyłącznie separatory grupujące typu spacja/NBSP/wąska, "_" i "'"
  //    (NIE usuwamy kropki/przecinka — bo to może być separator dziesiętny, np. 12,37)
  return s.replace(/[ \u00A0\u202F\u2007_']/g, "");
}



/* =================== Komponent =================== */
export default function FractionInput({
  value,
  onChange,
  disabled,
  label: inputLabel = "a",
  suffixLatex = "",
  placeholder = "np. 3/4, 1 1/2, 0.75",
  previewFormatter,
  powerPreview = false,
  sciPreview = false,
  showPreview = true,
  panelWidthDesktop = PANEL_WIDTH_DESKTOP,
  indentPx = INDENT,
}) {
  const label = normalizePreviewLabel(inputLabel);
  const pickString = (v) => {
    if (typeof v === "string") return v;
    if (v == null) return "";
    if (typeof v === "object") {
      if (typeof v.raw === "string") return v.raw;
      if (v.raw && typeof v.raw === "object" && typeof v.raw.raw === "string") {
        return v.raw.raw;
      }
      for (const key of ["value", "text", "input", "expr", "latex"]) {
        if (typeof v[key] === "string") return v[key];
      }
      return "";
    }
    return String(v);
  };
  const [local, setLocal] = useState(pickString(value));
  const [kbdOpen, setKbdOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setLocal(pickString(value)); }, [value]);
useEffect(() => {
  const normalizedRaw = normalizeIntegerLikeRaw(local);
  const parsed = parseFractionLikeInput(normalizedRaw);
  onChange && onChange({ raw: normalizedRaw, parsed });
}, [local]); // eslint-disable-line react-hooks/exhaustive-deps


  const setCaret = (pos) => { const el = inputRef.current; if (!el) return; el.focus(); el.setSelectionRange(pos, pos); };
  const insertAtCursor = (text) => {
    const el = inputRef.current; if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const next = local.slice(0, s) + text + local.slice(e);
    setLocal(next); setTimeout(() => setCaret(s + text.length), 0);
  };

  const handleKbdAction = (type) => {
    const el = inputRef.current; if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    if (type === "insert") return; // obsługujemy w SetKeyboard przez payload
  };

  /* Podgląd: sci > power > zwykły */
  const previewLatex = previewFormatter ? previewFormatter(local, label) :
    (sciPreview ? formatSciLatexIfMatch(local) : null)
    ?? (powerPreview ? formatPowerLatexIfMatch(local) : null)
    ?? `${formatLatexPreview(local, label)}${suffixLatex ? ` \\, ${suffixLatex}` : ""}`;

  /* ===== Layout: input szeroki jak panel ===== */
/* …fragment niezmieniony… */

  // wrapper: maxWidth = panel + indent (desktop); na mobile = 100%
  const containerStyle = {
    width: "100%",
    maxWidth: panelWidthDesktop + indentPx,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-start",
  };

  // Input jest juz w kolumnie po ikonie, wiec wypelnia szerokosc panelu.
  const inputStyle = { width: "100%", minWidth: 0, flex: "0 0 auto", boxSizing: "border-box" };

  return (
    <div style={containerStyle}>
      {/* wiersz: [ikona][pole] */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${indentPx}px 1fr`,
          columnGap: 0,            // ⇦ zmniejszony odstęp (było 8)
          alignItems: "center",
          width: "100%",
        }}
      >
        <SetKeyboard
          render="toggle"
          targetRef={inputRef}
          open={kbdOpen}
          onOpenChange={setKbdOpen}
          /* reszta bez zmian */
        />
        <input
          ref={inputRef}
          type="text"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          className="input-secondary"
          style={inputStyle}
        />
      </div>


      {kbdOpen && (
        <div style={{ width: "100%" }}>
          {/* panel klawiatury odsunięty o ten sam INDENT -> idealnie pod inputem */}
          <SetKeyboard
            render="panel"
            targetRef={inputRef}
            open={kbdOpen}
            onOpenChange={setKbdOpen}
            style={{ width: "100%" }}
            onAction={(type, payload) => {
              if (type === "insert") insertAtCursor(String(payload ?? ""));
              if (type === "backspace") {
                const el = inputRef.current; if (!el) return;
                const s = el.selectionStart, e = el.selectionEnd;
                if (s !== e) { const next = local.slice(0, s) + local.slice(e); setLocal(next); setTimeout(() => setCaret(s), 0); }
                else if (s > 0) { const next = local.slice(0, s - 1) + local.slice(e); setLocal(next); setTimeout(() => setCaret(s - 1), 0); }
                return;
              }
              if (type === "moveLeft")  { const el = inputRef.current; const s = el.selectionStart, e = el.selectionEnd; setTimeout(() => setCaret(Math.max(0, Math.min(s, e) - 1)), 0); }
              if (type === "moveRight") { const el = inputRef.current; const s = el.selectionStart, e = el.selectionEnd; setTimeout(() => setCaret(Math.min(local.length, Math.max(s, e) + 1)), 0); }
              if (type === "clear")     { setLocal(""); setTimeout(() => setCaret(0), 0); }
            }}
            compact
            panelIndent={INDENT}
          />
        </div>
      )}

      {showPreview && local && previewLatex && (
        <div style={{ marginTop: 6, marginBottom: 0, pointerEvents: "none" }}>
          <strong>Podgląd:</strong>{" "}
          <InlineMath math={previewLatex} />
        </div>
      )}
    </div>
  );
}

export { parseFractionLikeInput, formatLatexPreview, formatFractionLatexCore };
